import React, { useState } from "react";
import { Typography, Divider, Row, Col, List } from "antd";
import type { CancellationPolicy } from "@common-types";
import { ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, SafetyOutlined, InfoCircleOutlined, MessageOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import { Booking, MeetingPickup, CheckAvailability, EmptySearchMessage } from "@components";
import { ActivitySearchWrapper, Reviews, Itinerary } from "@share/components";
import { SkeletonComponent } from "../skeleton";
import "./style.scss";
import { cleanHtml, getAccountUsernameFromPath, GetHomeParams, navigateTo, RootState } from '@share/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { CarouselImage } from "../crowsel-image";
import { CarouselImageModal } from "../crowsel-image-modal";
import { Link, useLocation } from "react-router-dom";
import { Routes, Languages } from "@share/constants";
import { IAccount } from "@share/common-types";
import { isEmpty } from "lodash";
import moment from "moment";


const { Title, Text } = Typography;

export const DetailWrapper: React.FC<{ isStatic: boolean }> = ({ isStatic }) => {
  const intl = useIntl();

  const { activity, loading } = useSelector((state: RootState) => state.activityDetailsStore);
  const { data: availabilityData, selectedRateKey } = useSelector((state: RootState) => state.activityAvailabilityStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const { account } = loginStore;
  const location = useLocation();

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const accountName = getAccountUsernameFromPath(location);

  const isValidDate = (date?: string) =>
    Boolean(date && !date.startsWith("0001"));

  const formatDate = (date: string) => {
    if (!date) return date ?? '';
    const parsed = moment(date.split('T')[0], 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.format('MMMM D, YYYY') : date;
  };

  const onSearch = () => {
    const accountName = getAccountUsernameFromPath(location);
    const homeParams = GetHomeParams(account as IAccount);
    navigateTo(`/${accountName}${Routes.ActivitiesSearch}${homeParams}`);
  };

  if (!loading && (!activity || Object.keys(activity).length === 0)) {
    return (
      <div className="activity-details is-empty">

        <div className='activity-search-wrapper after-search' style={{ backgroundImage: 'url("/src/assets-share/images/hotel/bg-hotels.png")' }}>
          <ActivitySearchWrapper onSearch={onSearch} />
        </div>
        <div className="activity-details__empty-wrapper">
          <EmptySearchMessage
            label="activity.sorry.could.not.find.the.specified"
            account={account as IAccount}
          />
        </div>
      </div>
    );
  }

  const content = activity?.content;
  const modalities = activity?.modalities;
  const review = activity?.reviews?.total;
  const rating = activity?.reviews?.averageRating;
  const metric = modalities?.[0]?.durationMetric;
  const formattedMetric = metric ? metric.charAt(0).toUpperCase() + metric.slice(1).toLowerCase() : "";
  const durationValue = modalities?.[0]?.durationValue;
  const duration = durationValue ? `${durationValue} ${formattedMetric}` : "";
  const itineraryItems = content?.itineraryItems3 || content?.itineraryItems;

  const getLanguageName = (code: string) => {
    const lang = Languages.find(l => l.code.toLowerCase() === code.toLowerCase());
    if (lang) {
      // Namespaced: RSI's `languages.*` are the native names its language picker shows
      // ("Espanol"), while the activities front lists them in the reader's language
      // ("Spanish"), which is what belongs under "Offered in:".
      const id = `activity.languages.${lang.name.toLowerCase()}`;
      return intl.formatMessage({ id, defaultMessage: lang.name });
    }
    return code.charAt(0).toUpperCase() + code.slice(1);
  };

  const languages = Array.from(
    new Set(
      modalities
        ?.flatMap((m) => m.languageGuides || [])
        .map((lg) => lg.language)
        .filter(Boolean)
    )
  )
    .map((code) => getLanguageName(code as string))
    .join(", ");

  // Prefer the rate matching the selected day from check-availability; fall back to first rate.
  const allRates = availabilityData?.modalities?.flatMap((m: any) => m.rates ?? []) ?? [];
  const selectedAvailRate = selectedRateKey
    ? allRates.find((r: any) => r.rateKey === selectedRateKey)
    : null;
  const rate = selectedAvailRate ?? modalities?.[0]?.rates?.[0];
  const cancellationPolicies = rate?.cancellationPolicies;
  const cancellationPolicyText = rate?.cancellationPolicyText || intl.formatMessage({ id: 'cancellation.not_refundable.message.policy', defaultMessage: 'In the event of no-show, your booking will not qualify for a refund.\n There are no cancellation or modifications allowed for this reservation.' });
  const refundability = rate?.refundable;
  const isRefundableCancellationPolicy = (cancellationPolicies?.length ?? 0) > 0 && !!refundability;
  const isNonRefundableCancellationPolicy = !refundability && !isEmpty(cancellationPolicyText);
  const displayCancellationPolicy = isRefundableCancellationPolicy || isNonRefundableCancellationPolicy;

  return (

    <div className="activity-details">
      {loading ? (
        <SkeletonComponent />
      ) : (
        <>
          <div className="activity-details__back-button-search" >
            <ArrowLeftOutlined />
            <Link to={`/${accountName}${Routes.ActivitiesSearch}${location.search}`}>
              <FormattedMessage id="details.backToSearch" />
            </Link>
          </div>
          <CarouselImage
            images={content?.mediaImages || []}
            activityName={activity?.name}
            isGalleryOpen={isGalleryOpen}
            name={activity?.name}
            review={review as number}
            rating={rating as number}
            activity={activity as any}
            onOpenGallery={(index) => {
              setGalleryIndex(index);
              setIsGalleryOpen(true);
            }}
          />

          <div className="activity-info-bar">
            {duration && (
              <div className="info-item">
                <ClockCircleOutlined className="info-icon" />
                <span>{duration} <FormattedMessage id="activity.details.approx" defaultMessage="(approx.)" /></span>
              </div>
            )}
            
            {languages && (
              <div className="info-item">
                <MessageOutlined className="info-icon" />
                <span><FormattedMessage id="activity.details.offeredIn" defaultMessage="Offered in:" /> {languages}</span>
              </div>
            )}
          </div>

          <div className="activity-content">
            <Row gutter={24}>
              <Col xs={24} xl={16}>

                <div className="main-content check-availability-section">
                  <CheckAvailability activityId={activity?.code} />
                  <div className="content-section">

                    {content?.summary?.trim() && (
                      <>
                        <Title level={3}><FormattedMessage id="activity.details.allOverview" defaultMessage="Overview" /></Title>
                        <div
                          className="activity-summary"
                          dangerouslySetInnerHTML={{
                            __html: content?.summary || "",
                          }}
                        />
                      </>
                    )}

                    {(content?.highlights?.length ?? 0) > 0 && (
                      <div className="activity-highlights">
                        <Title level={3}><FormattedMessage id="activity.details.highlights" defaultMessage="Highlights" /></Title>
                        <List
                          dataSource={content?.highlights}
                          renderItem={(item) =>
                            item?.trim() ? (
                              <List.Item>
                                <CheckCircleOutlined className="highlight-icon" />
                                <Text>{item}</Text>
                              </List.Item>
                            ) : null
                          }
                        />
                      </div>
                    )}

                    {content?.description?.trim() && (
                      <div className="content-section">
                        <Title level={3}><FormattedMessage id="activity.details.description" defaultMessage="Description" /></Title>
                        <div
                          className="activity-description"
                          dangerouslySetInnerHTML={{
                            __html: cleanHtml(content?.description || ""),
                          }}
                        />
                      </div>
                    )}

                    {((content?.inclusions?.length ?? 0) > 0 || (content?.exclusions?.length ?? 0) > 0) && (
                      <>
                        <Divider />
                        <div className="content-section inclusions-exclusions-container">
                          <Title level={3}><FormattedMessage id="activity.details.whatsIncluded" defaultMessage="What's Included" /></Title>
                          <Row gutter={[24, 0]}>
                            {(content?.inclusions?.length ?? 0) > 0 && (
                              <Col xs={24} md={(content?.exclusions?.length ?? 0) > 0 ? 12 : 24}>
                                <List
                                  className="cancellation-policy-list"
                                  dataSource={content?.inclusions || []}
                                  renderItem={(item) => (
                                    <List.Item>
                                      <CheckCircleOutlined className="included-icon" />
                                      <Text>{item}</Text>
                                    </List.Item>
                                  )}
                                />
                              </Col>
                            )}
                            {(content?.exclusions?.length ?? 0) > 0 && (
                              <Col xs={24} md={(content?.inclusions?.length ?? 0) > 0 ? 12 : 24}>
                                <List
                                  dataSource={content?.exclusions || []}
                                  renderItem={(item) => (
                                    <List.Item>
                                      <CloseCircleOutlined className="excluded-icon" />
                                      <Text>{item}</Text>
                                    </List.Item>
                                  )}
                                />
                              </Col>
                            )}
                          </Row>
                        </div>
                      </>
                    )}

                    {(content?.startingPoints?.length || content?.travelerPickups?.length || content?.endPoints?.length) ? (
                      <>
                        <Divider />
                        <MeetingPickup
                          startingPoints={content?.startingPoints}
                          pickupPoints={content?.travelerPickups}
                          endPoints={content?.endPoints}
                        />
                      </>
                    ) : null}

                    {itineraryItems && (itineraryItems?.length ?? 0) > 0 && (
                      <>
                        <Divider />
                        <div className="content-section">
                          <Title level={3}><FormattedMessage id="activity.details.itinerary" defaultMessage="Itinerary" /></Title>
                          <Itinerary items={itineraryItems} />
                        </div>
                      </>
                    )}

                    {(content?.importantInfo?.length ?? 0) > 0 && (
                      <>
                        <Divider />
                        <div className="content-section">
                          <Title level={3}>
                            <FormattedMessage
                              id="activity.details.importantInfo"
                              defaultMessage="Important Information"
                            />
                          </Title>

                          <List
                            className="important-info-list"
                            dataSource={content?.importantInfo || []}
                            renderItem={(info, index) => (
                              <List.Item key={index}>
                                <InfoCircleOutlined className="info-icon" />
                                <span
                                  className="important-info-text"
                                  dangerouslySetInnerHTML={{
                                    __html: cleanHtml(info || ""),
                                  }}
                                />
                              </List.Item>
                            )}
                          />
                        </div>
                      </>
                    )}

                    {displayCancellationPolicy && (
                      <>
                        <Divider />
                        <Title level={3}>
                          <FormattedMessage id="activity.details.cancellationPolicy" defaultMessage="Cancellation Policy" />
                        </Title>

                        {isRefundableCancellationPolicy && (
                          <List
                            className="cancellation-policy-list"
                            dataSource={cancellationPolicies || []}
                            renderItem={(policy: CancellationPolicy) => {
                              if (!policy?.from || !isValidDate(policy.from)) {
                                return null;
                              }

                              const amount = Number(policy.amount || 0);

                              return (
                                <List.Item>
                                  <SafetyOutlined className="policy-icon" />
                                  <Text>
                                    {amount === 0 ? (
                                      <>
                                        <FormattedMessage id="activity.details.freeCancellationUntil" defaultMessage="Free cancellation until" />{" "}
                                        <strong>
                                          {formatDate(policy.from)}
                                        </strong>
                                      </>
                                    ) : (
                                      <FormattedMessage
                                        id="activity.details.cancellationFeeApply"
                                        defaultMessage="A fee of {amount} will apply for cancellations made on or after {date}."
                                        values={{
                                          amount: <strong>{policy.currency} {amount.toFixed(2)}</strong>,
                                          date: <strong>{formatDate(policy.from)}</strong>
                                        }}
                                      />
                                    )}
                                  </Text>
                                </List.Item>
                              );
                            }}
                          />)}

                        {isNonRefundableCancellationPolicy && (
                          <div className="cancellation-policy-text">
                            <SafetyOutlined className="policy-icon" style={{ color: 'rgb(0, 135, 104)', fontSize: '18px', marginRight: '12px' }} />
                            <Text>{cancellationPolicyText}</Text>
                          </div>)}
                      </>)}

                  </div>

                  {(review ?? 0) > 0 && (
                    <>
                      <Divider />
                      {activity && <Reviews productCode={activity.code} />}
                    </>
                  )}
                </div>
              </Col>

              <Col xs={24} xl={8}>
                {activity && (
                  <Booking
                    activities={activity}
                    isStatic={isStatic}
                  />
                )}
              </Col>
            </Row>
          </div>

          <CarouselImageModal
            visible={isGalleryOpen}
            images={content?.mediaImages || []}
            initialIndex={galleryIndex}
            onClose={() => setIsGalleryOpen(false)}
          />
        </>
      )}
    </div>
  );
};
